// Modified Hotel.js with fixed loading state
import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc, collection, addDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrash,
  faMapMarker,
  faCar,
  faPhone,
  faPlus,
  faSpinner,
  faHotel,
  faChevronRight,
  faChevronLeft,
  faInfoCircle,
  faCalendarAlt,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import { useTheme } from '../../context/ThemeContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import HotelSearchModal from './HotelSearchModal';
import moment from 'moment';
import defaultHotelImage from '../../assets/Images/defaulthotelpicture.png';
import styles from './Hotel.module.css';

const Hotel = () => {
  const { selectedEvent, getSelectedDate, updateDate } = useContext(TourContext);
  const { userData } = useContext(UserContext);
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [editingHotel, setEditingHotel] = useState(null);
  
  // State for Room management
  const [selectedHotelIndex, setSelectedHotelIndex] = useState(null);
  const [currentHotel, setCurrentHotel] = useState(null);

  const eventDate = getSelectedDate();
  const eventType = selectedEvent?.type || 'tour';

  // Load hotels from the new structure
  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates after unmount
    
    const loadHotels = async () => {
      // Don't set loading if we don't have required IDs
      if (!selectedEvent?.id || !eventDate?.id) {
        if (isMounted) {
          setHotels([]);
        }
        return;
      }

      if (isMounted) {
        setIsLoading(true);
        setLoadingText('Loading hotels...');
      }

      try {
        const db = getFirestoreInstance();
        const collectionName = eventType === 'tour' ? 'tours' : 'shows';
        
        console.log(`Fetching hotels for ${eventType} ${selectedEvent.id}, date ${eventDate.id}`);
        
        // Get hotels from the subcollection
        const hotelsQuery = query(
          collection(db, collectionName, selectedEvent.id, 'eventDates', eventDate.id, 'hotels')
        );
        
        const hotelsSnapshot = await getDocs(hotelsQuery);
        console.log(`Found ${hotelsSnapshot.size} hotels`);
        
        const hotelsData = [];
        
        // Process each hotel
        for (const hotelDoc of hotelsSnapshot.docs) {
          const hotelData = {
            id: hotelDoc.id,
            ...hotelDoc.data(),
            rooms: 0,
            roomDetails: []
          };
          
          // Get rooms for this hotel
          const roomsQuery = query(
            collection(db, collectionName, selectedEvent.id, 'eventDates', eventDate.id, 'hotels', hotelDoc.id, 'rooms')
          );
          
          const roomsSnapshot = await getDocs(roomsQuery);
          
          if (!roomsSnapshot.empty) {
            hotelData.rooms = roomsSnapshot.size;
            hotelData.roomDetails = roomsSnapshot.docs.map(roomDoc => ({
              id: roomDoc.id,
              ...roomDoc.data()
            }));
          }
          
          hotelsData.push(hotelData);
        }
        
        if (isMounted) {
          setHotels(hotelsData);
        }
      } catch (error) {
        console.error('Error loading hotels:', error);
        if (isMounted) {
          setHotels([]);
        }
      } finally {
        // Always set loading to false in finally block if component is still mounted
        if (isMounted) {
          setIsLoading(false);
          setLoadingText('');
        }
      }
    };

    loadHotels();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [selectedEvent, eventDate, eventType]);


 

  const handleDeleteHotel = async (hotelId) => {
    if (!window.confirm('Are you sure you want to delete this hotel?')) return;

    setIsLoading(true);
    setLoadingText('Deleting hotel...');

    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // First, delete all rooms in this hotel
      const roomsQuery = query(
        collection(db, collectionName, selectedEvent.id, 'eventDates', eventDate.id, 'hotels', hotelId, 'rooms')
      );
      
      const roomsSnapshot = await getDocs(roomsQuery);
      
      const batch = db.batch();
      
      // Add all room deletions to batch
      roomsSnapshot.forEach(roomDoc => {
        const roomRef = doc(db, collectionName, selectedEvent.id, 'eventDates', eventDate.id, 'hotels', hotelId, 'rooms', roomDoc.id);
        batch.delete(roomRef);
      });
      
      // Add hotel deletion to batch
      const hotelRef = doc(db, collectionName, selectedEvent.id, 'eventDates', eventDate.id, 'hotels', hotelId);
      batch.delete(hotelRef);
      
      // Commit all deletions
      await batch.commit();

      // Update local state
      setHotels(prev => prev.filter(hotel => hotel.id !== hotelId));
      
      // Update the date to trigger listeners
      await updateDate(selectedEvent.id, eventDate.id, eventType);
    } catch (error) {
      console.error('Error deleting hotel:', error);
      alert('Failed to delete hotel. Please try again.');
    } finally {
      setIsLoading(false);
      setLoadingText('');
    }
  };

  const handleEditHotel = useCallback((hotelId) => {
    const hotelToEdit = hotels.find(hotel => hotel.id === hotelId);
    if (hotelToEdit) {
      setEditingHotel(hotelToEdit);
      setIsModalVisible(true);
    }
  }, [hotels]);

  const getVisibilityText = useCallback((hotel) => {
    switch (hotel.visibility) {
      case 'All':
        return 'Everyone';
      case 'Artist':
        return 'Artists Only';
      case 'Crew':
        return 'Crew Only';
      case 'Me':
        return 'Only Me';
      case 'Custom':
        if (hotel.selectedUsers?.length > 0) {
          const names = hotel.selectedUsers.join(', ');
          return names.length > 30 ? `${names.substring(0, 27)}...` : names;
        }
        return 'Custom Users';
      default:
        return 'Everyone';
    }
  }, []);

  const handleViewVenueProfile = (hotel, event) => {
    event.stopPropagation();
    if (hotel?.venueDetails?.venueData?.id) {
      navigate(`/venue/${hotel.venueDetails.venueData.id}`);
    }
  };

  const handleMapClick = (hotel) => {
    if (!hotel?.venueDetails?.venueData?.googleMapsUri) {
      alert('Map link not available');
      return;
    }
    window.open(hotel.venueDetails.venueData.googleMapsUri, '_blank');
  };

  const handleRideClick = (hotel) => {
    alert('Ride functionality coming soon');
  };

  const handleCallClick = (hotel) => {
    if (!hotel?.venueDetails?.venueData?.nationalPhoneNumber) {
      alert('Phone number not available');
      return;
    }
    window.location.href = `tel:${hotel.venueDetails.venueData.nationalPhoneNumber}`;
  };

  const handleViewRooms = (hotel) => {
    if (selectedEvent && eventDate) {
      navigate(`/${eventType}/${selectedEvent.id}/${eventDate.id}/hotel/${hotel.id}`);
    }
  };

  const renderHotel = useCallback((hotel) => {
    if (!hotel?.venueDetails?.venueData) return null;
  
    const {
      displayName,
      formattedAddress,
      placeCoverPhotoUrl
    } = hotel.venueDetails.venueData;
  
    const checkInMoment = moment(hotel.checkInDateTime);
    const checkOutMoment = moment(hotel.checkOutDateTime);
  
    return (
      <div key={hotel.id} className={styles.hotelCard}>
        <div className={styles.groupHeader}>
          <div className={styles.groupInfo}>
            <div className={styles.groupName}>
              {hotel.group === 'Custom' && hotel.customGroupName
                ? hotel.customGroupName
                : hotel.group}
            </div>
            <div className={styles.visibilityText}>
              Visible to: {getVisibilityText(hotel)}
            </div>
          </div>
          <div className={styles.groupActions}>
            <button 
              className={`${styles.actionButton}`}
              onClick={() => handleEditHotel(hotel.id)}
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button 
              className={`${styles.actionButton}`}
              onClick={() => handleDeleteHotel(hotel.id)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>
  
        <div className={styles.hotelContent}>
          <div className={styles.hotelHeader}>
            <img
              src={placeCoverPhotoUrl || defaultHotelImage}
              alt={displayName}
              className={styles.hotelImage}
            />
            <div className={styles.hotelTimeDetails}>
              <div className={styles.timeBlock}>
                <span className={styles.timeLabel}>Check-In</span>
                <span className={styles.timeDate}>{checkInMoment.format('DD MMM')}</span>
                <span className={styles.timeHour}>{checkInMoment.format('h:mm A')}</span>
              </div>
  
              <div className={styles.timeBlock}>
                <span className={styles.timeLabel}>Check-Out</span>
                <span className={styles.timeDate}>{checkOutMoment.format('DD MMM')}</span>
                <span className={styles.timeHour}>{checkOutMoment.format('h:mm A')}</span>
              </div>
  
              <div className={styles.timeBlock}>
                <span className={styles.timeLabel}>Rooms</span>
                <span className={styles.roomCount}>{hotel.rooms || 0}</span>
              </div>
            </div>
          </div>
  
          <div className={styles.hotelInfo}>
            <h3 className={styles.hotelName}>{displayName}</h3>
            <p className={styles.hotelAddress}>{formattedAddress}</p>
            <button 
              className={styles.viewRatingsButton}
              onClick={(e) => handleViewVenueProfile(hotel, e)}
            >
              <FontAwesomeIcon icon={faEye} />
              <span>View Ratings & Details</span>
            </button>
          </div>
  
          <div className={styles.hotelActions}>
            <button 
              className={styles.actionButton} 
              onClick={() => handleMapClick(hotel)}
            >
              <FontAwesomeIcon icon={faMapMarker} />
              <span>View Map</span>
            </button>
            <button 
              className={styles.actionButton} 
              onClick={() => handleRideClick(hotel)}
            >
              <FontAwesomeIcon icon={faCar} />
              <span>Get a Ride</span>
            </button>
            <button 
              className={styles.actionButton} 
              onClick={() => handleCallClick(hotel)}
            >
              <FontAwesomeIcon icon={faPhone} />
              <span>Call</span>
            </button>
          </div>
          
          <div className={styles.hotelRooms} onClick={() => handleViewRooms(hotel)}>
            <div className={styles.roomsHeader}>
              <h4 className={styles.roomsTitle}>Rooms</h4>
              <div className={styles.roomsCount}>{hotel.rooms || 0}</div>
            </div>
            
            {hotel.roomDetails && hotel.roomDetails.length > 0 ? (
              <div className={styles.roomsPreview}>
                {hotel.roomDetails.slice(0, 2).map((room, roomIndex) => (
                  <div key={roomIndex} className={styles.roomPreviewItem}>
                    <div className={styles.roomPreviewName}>{room.name}</div>
                    <div className={styles.roomPreviewInfo}>
                      #{room.roomNumber || 'N/A'}
                      {room.bagTag && <span> • Tag: {room.bagTag}</span>}
                    </div>
                  </div>
                ))}
                {hotel.roomDetails.length > 2 && (
                  <div className={styles.roomsMore}>
                    +{hotel.roomDetails.length - 2} more rooms
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.emptyRooms}>
                <FontAwesomeIcon icon={faHotel} className={styles.emptyIcon} />
                <span className={styles.addText}>Add Rooms</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [getVisibilityText, handleEditHotel, handleMapClick, handleRideClick, handleCallClick, handleViewRooms, selectedEvent, eventDate, navigate, eventType]);

  if (isLoading) {
    console.log("Rendering loading state");
    return (
      <div className={styles.loadingContainer}>
        <FontAwesomeIcon icon={faSpinner} spin className={styles.loadingSpinner} />
        <p className={styles.loadingText}>{loadingText || 'Loading...'}</p>
      </div>
    );
  }
  
  console.log(`Rendering main view, ${hotels.length} hotels found`);
  return (
    <div className={styles.hotelContainer}>
      <button
        className={styles.addHotelButton}
        onClick={() => setIsModalVisible(true)}
      >
        <FontAwesomeIcon icon={faPlus} />
        Add Hotel
      </button>
  
      {hotels.length === 0 ? (
        <div className={styles.emptyState}>
          <FontAwesomeIcon icon={faHotel} className={styles.emptyStateIcon} />
          <p className={styles.emptyText}>No hotels added yet</p>
        </div>
      ) : (
        <div className={styles.hotelsList}>
          {hotels.map(hotel => renderHotel(hotel))}
        </div>
      )}
  
      {isModalVisible && (
        <HotelSearchModal
          isOpen={isModalVisible}
          onClose={() => {
            setIsModalVisible(false);
            setEditingHotel(null);
          }}
          onSave={async () => {
            // The HotelSearchModal will update Firestore directly
            // We just need to make sure the listener updates the state
            await updateDate(selectedEvent.id, eventDate.id, eventType);
          }}
          editingHotel={editingHotel}
          eventData={selectedEvent}
          dateId={eventDate?.id}
          eventType={eventType}
          membershipTier={userData?.membershipTier}
        />
      )}
  
      {loadingText && (
        <div className={styles.loadingOverlay}>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" color="white" />
          <p className={styles.overlayText}>{loadingText}</p>
        </div>
      )}
    </div>
  );
};

export default Hotel;